import React, { memo, useCallback } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { UserTypes } from "@gethere/common/enums";
import Avatar from "../Avatar";
import { useUser } from "../../contexts/UserContext";
import client from "../../utils/client";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Briefcase, CircleUserIcon, LogOutIcon } from "lucide-react";
import { NavigationLink } from "./NavigationLink";
import { ThemeSwitcher, LanguageSwitcher } from "./ThemeAndLanguageSwitcher";

interface UserAvatarMenuProps {
  personal?: boolean;
  business?: boolean;
  className?: string;
}

const GuestActions = memo(() => {
  const intl = useIntl();
  
  return (
    <div className="flex flex-row items-center gap-3">
      <NavigationLink to="/login">
        {intl.formatMessage({ id: "login_title", defaultMessage: "Connect" })}
      </NavigationLink>
      <ThemeSwitcher variant="icon" />
      <LanguageSwitcher variant="icon" />
    </div>
  );
});

GuestActions.displayName = "GuestActions";

const UserDropdownContent = memo<{
  name: string;
  showManageLink: boolean;
  onLogout: () => void;
}>(({ name, showManageLink, onLogout }) => {
  const intl = useIntl();

  return (
    <DropdownMenuContent align="end" className="min-w-[180px]">
      <DropdownMenuItem disabled>{name}</DropdownMenuItem>
      
      {showManageLink && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link className="gap-3 cursor-pointer" to="/businesses">
              <Briefcase className="w-3 h-3" />
              {intl.formatMessage({ id: "manage", defaultMessage: "Manage" })}
            </Link>
          </DropdownMenuItem>
        </>
      )}
      
      <DropdownMenuSeparator />
      
      <DropdownMenuItem className="gap-3 cursor-pointer">
        <CircleUserIcon className="w-3 h-3" />
        <Link to="/me">Profile</Link>
      </DropdownMenuItem>
      
      <ThemeSwitcher variant="dropdown" />
      <LanguageSwitcher variant="dropdown" />
      
      <DropdownMenuSeparator />
      
      <DropdownMenuItem
        onClick={onLogout}
        className="gap-3 cursor-pointer"
      >
        <LogOutIcon className="w-3 h-3" />
        {intl.formatMessage({ id: "settings_items_logout_title", defaultMessage: "Logout" })}
      </DropdownMenuItem>
    </DropdownMenuContent>
  );
});

UserDropdownContent.displayName = "UserDropdownContent";

export const UserAvatarMenu: React.FC<UserAvatarMenuProps> = memo(({
  personal = false,
  business = false,
  className,
}) => {
  const { user } = useUser();

  const handleLogout = useCallback(() => {
    client.logout();
  }, []);

  if (!user || user.type === UserTypes.GUEST) {
    return <GuestActions />;
  }

  const name =
    [user?.name?.first, user?.name?.last].filter(Boolean).join(" ") ||
    "Profile";
  
  const showManageLink = !personal && !business;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" size="icon" className={`rounded-full ${className}`}>
          <Avatar name={user?.name} image={user?.profileImg} />
          <span className="sr-only">Toggle user menu</span>
        </Button>
      </DropdownMenuTrigger>
      
      <UserDropdownContent
        name={name}
        showManageLink={showManageLink}
        onLogout={handleLogout}
      />
    </DropdownMenu>
  );
});

UserAvatarMenu.displayName = "UserAvatarMenu"; 